import { computed, makeObservable } from 'mobx';

import { moment, TimeFormat } from '@feathr/hooks';
import type { IBaseAttributes } from '@feathr/rachis';
import { DisplayModel } from '@feathr/rachis';

export interface IReportAttributes extends IBaseAttributes {
  date_created?: string;
  report_redirect_ids?: string[];
}

export type TReportKey =
  // Campaign
  | 'c'
  // Event
  | 'e'
  // Flight
  | 'f'
  // Segment
  | 's';

export abstract class ReportModel<
  IAttributes extends IReportAttributes,
> extends DisplayModel<IAttributes> {
  public abstract reportKey: TReportKey;

  constructor(attributes: Partial<IAttributes> = {}) {
    super(attributes);

    makeObservable(this);
  }

  @computed
  public get reportRangeStart(): string {
    return moment.utc(this.get('stats_start_date')).format(TimeFormat.isoDate);
  }

  public get reportRangeEnd(): string {
    // the stats_end_date returned by the backend is the date at which stats will stop being collected
    // for some models, there is no set end date, so this value will be something like 12/31/9999
    // regardless, we can't actually get stats for future dates, so we should constrain this value
    // the effective end date should never be earlier than the stats_start_date, and never later than today
    // also, the backend uses non-inclusive end dates, so we need to convert to inclusive by subtracting one day
    const statsStartDate = moment.utc(this.get('stats_start_date'));
    const statsEndDate = moment.utc(this.get('stats_end_date')).subtract(1, 'day');
    return moment
      .min(moment.utc(), moment.max(statsEndDate, statsStartDate))
      .format(TimeFormat.isoDate);
  }
}
